import { courseDraftByIdQueryKey, getCourseDraftById } from '@hendrx/core/courses/queries/getCourseDraftById';
import {
  getLearningActivityDraftById,
  learningActivityDraftByIdQueryKey
} from '@hendrx/core/learningActivity/queries/getLearningActivityDraftById';
import { getPBLDraftById, pblDraftByIdQueryKey } from '@hendrx/core/pbl/queries/getPBLDraftById';
import { getPlenaryDraftById, plenaryDraftByIdQueryKey } from '@hendrx/core/plenary/queries/getPlenaryDraftById';
import type { QueryKey } from '@tanstack/react-query';
import { notFound } from '@tanstack/react-router';

import { isApiErrorStatus } from 'shared/client/apiClient';

import { getQueryClient } from '@/app/getQueryClient';

/**
 * Which draft a Studio page is about, keyed by the route parameter that carries its id. The key is the one the
 * screen's own query hook uses, so the screen reads the entry the loader filled rather than fetching again.
 *
 * A new draft screen is covered by naming its parameter after one of these — there is no per-route line to add.
 */
const DRAFT_QUERY_BY_PARAM = {
  courseDraftId: (id: string) => ({
    queryKey: courseDraftByIdQueryKey({ courseDraftId: id }) as QueryKey,
    queryFn: () => getCourseDraftById(id)
  }),
  learningActivityDraftId: (id: string) => ({
    queryKey: learningActivityDraftByIdQueryKey({ learningActivityDraftId: id }) as QueryKey,
    queryFn: () => getLearningActivityDraftById(id)
  }),
  pblDraftId: (id: string) => ({
    queryKey: pblDraftByIdQueryKey({ pblDraftId: id }) as QueryKey,
    queryFn: () => getPBLDraftById({ pblDraftId: id })
  }),
  plenaryDraftId: (id: string) => ({
    queryKey: plenaryDraftByIdQueryKey({ plenaryDraftId: id }) as QueryKey,
    queryFn: () => getPlenaryDraftById(id)
  })
};

type DraftParam = keyof typeof DRAFT_QUERY_BY_PARAM;

const DRAFT_PARAMS = Object.keys(DRAFT_QUERY_BY_PARAM) as DraftParam[];

/**
 * The `/studio` route's `loader`: read the draft the page is about before anything renders, and answer a 404
 * with the router's not-found page (spec 008 FR-021a, D-17 §6).
 *
 * `params` is typed for `/studio` alone, but a parent loader receives the whole match's parameters, so the
 * child's draft id is visible here. A page with no draft parameter — the dashboard, "new", templates — is a
 * no-op and makes no request.
 *
 * The server answers a draft owned by another organization exactly as one that does not exist, so a 404 is the
 * whole access decision on this side. Deciding it here rather than in each screen means no provider mounts and
 * no collaboration socket opens for a refused draft, and `defaultNotFoundComponent` is the one place the page
 * renders. Not a redirect to `/unauthorized`: that route signs the user out, and asking for something outside
 * your workspace is not an authentication failure (FR-013b).
 *
 * Any other failure is left to the screen, which already renders its own fetch-error state from the same
 * cached query; throwing it here would replace that with the router's error page.
 */
export const loadDraftOrNotFound = async (params: object): Promise<void> => {
  const routeParams = params as Partial<Record<DraftParam, string>>;
  const param = DRAFT_PARAMS.find(name => routeParams[name]);

  if (!param) {
    return;
  }

  try {
    // One attempt. A 404 is never retried anyway, and a transient failure is the screen's to retry — retrying
    // here too would hold the page on a blank route for the whole backoff and then again inside the screen.
    await getQueryClient().ensureQueryData({
      ...DRAFT_QUERY_BY_PARAM[param](routeParams[param] as string),
      retry: false
    });
  } catch (error) {
    if (isApiErrorStatus(error, 404)) {
      throw notFound();
    }
  }
};
